import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db, functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { getPublicSiteSettings, PublicSiteSettings, ContentTitleIconSettings } from '../api/publicPages';
import { waitUntilWidgetOpened } from '../utils/widgetOpenedGate';

interface LanguageSpecificSettings {
  tabTitle?: string;
  title?: string;
  logoUrl?: string;
  footerText?: string;
  contentTitle?: string;
  contentTitleIcon?: ContentTitleIconSettings;
  contentSubtitle?: string;
  contentPreInputNotice?: string;
  contentPreInputNoticeAlign?: 'left' | 'center' | 'right';
  contentPreInputNoticePaddingX?: number;
  contentPreInputNoticeRequireConsent?: boolean;
  contentPreInputNoticeConsentLabel?: string;
  contentPreInputNoticeConsentPlaceholder?: string;
  contentAlwaysInputNotice?: string;
  contentPlaceholder?: string;
  contentDisclaimer?: string;
  keywordsLabel?: string;
  textSizeLabels?: {
    small: string;
    medium: string;
    large: string;
    xlarge: string;
  };
}

interface PublicPageData {
  publishId: string;
  workspaceId: string;
  workspaceTitle: string;
  html: string | null;
  hasAISearch: boolean;
  publicRagAvailable: boolean;
  siteSettings: PublicSiteSettings | null;
  status: 'loading' | 'notfound' | 'hidden' | 'ready';
  isLoading: boolean;
  // 多言語機能
  selectedLanguage: string;
  availableLanguages: string[];
  languageSpecificSettings: LanguageSpecificSettings | null;
  setSelectedLanguage: (lang: string) => void;
  /** デモなど、ブラウザ言語より優先する初期言語（ISO 639-1） */
  preferredLanguage?: string;
}

const PublicPageContext = createContext<PublicPageData | null>(null);

export const usePublicPageData = () => {
  const context = useContext(PublicPageContext);
  if (!context) {
    throw new Error('usePublicPageData must be used within PublicPageProvider');
  }
  return context;
};

interface PublicPageProviderProps {
  children: ReactNode;
  /**
   * ルートパラメータ以外から publishId を渡したい場合に使用
   * 例: ウィジェット用ページ、管理画面プレビューなど
   */
  publishId?: string;
  /**
   * 利用言語に含まれるとき、ブラウザ言語より優先して初期選択する。
   * 例: デモページが親サイトから受け取った ?locale=
   */
  preferredLanguage?: string;
}

function primaryLanguageCode(raw: string | undefined): string {
  return (raw || '').trim().toLowerCase().split('-')[0];
}

export const PublicPageProvider: React.FC<PublicPageProviderProps> = ({ children, publishId: publishIdFromProps, preferredLanguage }) => {
  const { publishId: routePublishId } = useParams();
  const effectivePublishId = publishIdFromProps ?? routePublishId ?? '';
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [languageSpecificSettings, setLanguageSpecificSettings] = useState<LanguageSpecificSettings | null>(null);
  
  const [data, setData] = useState<PublicPageData>({
    publishId: effectivePublishId,
    workspaceId: '',
    workspaceTitle: '',
    html: null,
    hasAISearch: true,
    publicRagAvailable: true,
    siteSettings: null,
    status: 'loading',
    isLoading: true,
    selectedLanguage: '',
    availableLanguages: [],
    languageSpecificSettings: null,
    setSelectedLanguage: () => {},
  });

  useEffect(() => {
    const run = async () => {
      const startTime = performance.now();

      const publishId = effectivePublishId;

      if (!publishId) {

        setData(prev => ({ ...prev, status: 'notfound', isLoading: false }));
        return;
      }
      
      try {
        // Firestoreから公開ページデータを取得（一度だけ）
        const queryStartTime = performance.now();
        const ref = doc(db as any, 'publicPageURL', publishId);
        const snap = await getDoc(ref);
        const queryTime = performance.now() - queryStartTime;
        
        console.log('📄 PublicPageProvider: Firestoreクエリ完了', { 
          publishId,
          time: `${queryTime.toFixed(2)}ms`,
          exists: snap.exists()
        });
        
        if (!snap.exists()) {
          console.log('❌ PublicPageProvider: ドキュメントが存在しません', { 
            publishId,
            totalTime: `${(performance.now() - startTime).toFixed(2)}ms`
          });
          setData(prev => ({ ...prev, status: 'notfound', isLoading: false }));
          return;
        }
        
        const pageData = snap.data() as any;
        
        // 公開用リンクが ON のときだけ表示する。status と published の両方が公開でないと非公開。
        if (pageData.status !== 'public' || pageData.published !== true) {
          console.log('❌ PublicPageProvider: ページが非公開状態', { 
            publishId,
            status: pageData.status,
            published: pageData.published,
            totalTime: `${(performance.now() - startTime).toFixed(2)}ms`
          });
          setData(prev => ({ ...prev, status: 'hidden', isLoading: false }));
          return;
        }
        
        // ワークスペースIDを取得
        const wsId = pageData.workspaceId;
        
        // サイト設定を取得
        let siteSettings: PublicSiteSettings | null = null;
        let publicRagAvailable = true;
        if (wsId) {
          try {
            const settingsStartTime = performance.now();
            siteSettings = await getPublicSiteSettings(wsId);
            const settingsTime = performance.now() - settingsStartTime;
            
            console.log('⚙️ PublicPageProvider: サイト設定取得完了', { 
              publishId,
              workspaceId: wsId,
              time: `${settingsTime.toFixed(2)}ms`,
              found: !!siteSettings,
              favicon: siteSettings?.favicon || 'None',
              hasFavicon: !!siteSettings?.favicon,
              tabTitle: siteSettings?.tabTitle || 'None',
              hasTabTitle: !!siteSettings?.tabTitle,
              logoUrl: siteSettings?.logoUrl || 'None',
              hasLogo: !!siteSettings?.logoUrl,
              title: siteSettings?.title || 'None',
              hasTitle: !!siteSettings?.title
            });
            
          } catch (settingsError) {
            console.warn('❌ PublicPageProvider: サイト設定取得エラー', { 
              publishId,
              workspaceId: wsId,
              error: settingsError
            });
          }

          try {
            const creditSnap = await getDoc(doc(db as any, 'publicPages', wsId, 'settings', 'credit'));
            if (creditSnap.exists() && creditSnap.data()?.publicRagAvailable === false) {
              publicRagAvailable = false;
            }
          } catch (creditError) {
            console.warn('❌ PublicPageProvider: クレジット状態取得エラー', { publishId, workspaceId: wsId, error: creditError });
          }
        }
        // データを設定
        const newData: PublicPageData = {
          publishId,
          workspaceId: wsId || '',
          workspaceTitle: pageData.og?.title || pageData.workspaceTitle || '',
          html: pageData.snapshot || null,
          hasAISearch: pageData.enableAISearch !== false,
          publicRagAvailable,
          siteSettings,
          status: 'ready',
          isLoading: false,
          selectedLanguage,
          availableLanguages,
          languageSpecificSettings,
          setSelectedLanguage,
        };

        setData(newData);
        
        const totalTime = performance.now() - startTime;
        console.log('✅ PublicPageProvider: 全処理完了', { 
          publishId,
          totalTime: `${totalTime.toFixed(2)}ms`,
          breakdown: {
            query: `${queryTime.toFixed(2)}ms`,
            settings: siteSettings ? 'measured above' : 'skipped'
          },
          hasHtml: !!(pageData.snapshot),
          hasAISearch: pageData.enableAISearch !== false
        });

        // トラッキングをバックグラウンドで実行（awaitしない）
        const trackStartTime = performance.now();
        const track = httpsCallable(functions as any, 'trackPublicPageViewFn');
        // ウィジェット先読み（prefetch=1）中は、実際に開かれるまで閲覧数を加算しない
        waitUntilWidgetOpened()
          .then(() => track({ publishId, referer: document.referrer || '', userAgent: navigator.userAgent }))
          .then(() => {
            const trackTime = performance.now() - trackStartTime;
            console.log('📊 PublicPageProvider: トラッキング完了（バックグラウンド）', { 
              publishId,
              time: `${trackTime.toFixed(2)}ms`
            });
          })
          .catch((trackError) => {
            console.warn('⚠️ PublicPageProvider: トラッキングエラー（バックグラウンド）', { 
              publishId,
              error: trackError
            });
          });
        
      } catch (error) {
        console.error('❌ PublicPageProvider: エラー発生', { 
          publishId,
          error,
          totalTime: `${(performance.now() - startTime).toFixed(2)}ms`
        });
        setData(prev => ({ ...prev, status: 'notfound', isLoading: false }));
      }
    };
    
    run();
  }, [effectivePublishId]);

  // 多言語機能：利用可能な言語を取得し、ブラウザ言語を初期値に設定
  useEffect(() => {
    const fetchLanguages = async () => {
      if (!data.workspaceId || !data.siteSettings?.isMultilingual) {
        return;
      }

      try {
        const languagesRef = collection(db, 'publicPages', data.workspaceId, 'settings', 'site', 'languages');
        const languagesSnapshot = await getDocs(languagesRef);
        
        const languageDocsWithOrder = languagesSnapshot.docs.map(doc => ({
          code: doc.id,
          order: doc.data().order ?? 999
        }));
        
        // order順にソート
        languageDocsWithOrder.sort((a, b) => a.order - b.order);
        const languageCodes = languageDocsWithOrder.map(doc => doc.code);
        
        setAvailableLanguages(languageCodes);
        
        // ブラウザの言語を検出
        const browserLang = navigator.language || (navigator.languages && navigator.languages[0]) || 'en';
        const browserLangCode = browserLang.split('-')[0].toLowerCase();
        
        // 明示指定（デモの ?locale= など）→ ブラウザ言語 → デフォルト言語
        const preferredCode = primaryLanguageCode(preferredLanguage);
        const initialLang = preferredCode && languageCodes.includes(preferredCode)
          ? preferredCode
          : languageCodes.includes(browserLangCode)
            ? browserLangCode
            : (data.siteSettings.defaultLanguage || languageCodes[0]);
        
        setSelectedLanguage(initialLang);
        
        console.log('🌍 PublicPageProvider: 多言語設定を取得:', {
          availableLanguages: languageCodes,
          browserLanguage: browserLangCode,
          selectedLanguage: initialLang
        });
        
      } catch (error) {
        console.error('❌ PublicPageProvider: 言語リストの取得に失敗:', error);
      }
    };

    fetchLanguages();
  }, [data.workspaceId, data.siteSettings?.isMultilingual, data.siteSettings?.defaultLanguage, preferredLanguage]);

  // 選択言語が変更されたら、その言語固有のコンテンツを取得
  useEffect(() => {
    const fetchLanguageContent = async () => {
      if (!selectedLanguage || !data.workspaceId || !data.siteSettings?.isMultilingual) {
        setLanguageSpecificSettings(null);
        return;
      }

      try {
        const languageDocRef = doc(db, 'publicPages', data.workspaceId, 'settings', 'site', 'languages', selectedLanguage);
        const languageDoc = await getDoc(languageDocRef);
        
        if (languageDoc.exists()) {
          const langData = languageDoc.data();
          setLanguageSpecificSettings({
            tabTitle: langData.tabTitle,
            title: langData.title,
            logoUrl: langData.logoUrl,
            footerText: langData.footerText,
            contentTitle: langData.contentTitle,
            contentTitleIcon: langData.contentTitleIcon,
            contentSubtitle: langData.contentSubtitle,
            contentPreInputNotice: langData.contentPreInputNotice,
            contentPreInputNoticeAlign: langData.contentPreInputNoticeAlign,
            contentPreInputNoticePaddingX: langData.contentPreInputNoticePaddingX,
            contentPreInputNoticeRequireConsent: langData.contentPreInputNoticeRequireConsent,
            contentPreInputNoticeConsentLabel: langData.contentPreInputNoticeConsentLabel,
            contentPreInputNoticeConsentPlaceholder: langData.contentPreInputNoticeConsentPlaceholder,
            contentAlwaysInputNotice: langData.contentAlwaysInputNotice,
            contentPlaceholder: langData.contentPlaceholder,
            contentDisclaimer: langData.contentDisclaimer,
            keywordsLabel: langData.keywordsLabel,
            textSizeLabels: langData.textSizeLabels
          });
          
          console.log(`📖 PublicPageProvider: ${selectedLanguage} のコンテンツを取得:`, langData);
        }
      } catch (error) {
        console.error('❌ PublicPageProvider: 言語固有コンテンツの取得に失敗:', error);
      }
    };

    fetchLanguageContent();
  }, [selectedLanguage, data.workspaceId, data.siteSettings?.isMultilingual]);

  // ファビコンとタブタイトルの設定（言語切り替え時も反映）
  useEffect(() => {
    console.log('🎨 PublicPageProvider: ファビコン設定チェック', {
      hasFavicon: !!data.siteSettings?.favicon,
      faviconUrl: data.siteSettings?.favicon,
      hasTabTitle: !!(languageSpecificSettings?.tabTitle || data.siteSettings?.tabTitle),
      tabTitle: languageSpecificSettings?.tabTitle || data.siteSettings?.tabTitle
    });

    // ファビコンの設定（全言語共通）
    if (data.siteSettings?.favicon) {
      const faviconUrl = data.siteSettings.favicon;
      console.log('🔄 PublicPageProvider: ファビコンを設定中:', faviconUrl);

      // 既存のfaviconをすべて削除
      const iconSelectors = [
        'link[rel="icon"]',
        'link[rel="shortcut icon"]',
        'link[rel="apple-touch-icon"]'
      ];
      const existingIcons = document.head.querySelectorAll<HTMLLinkElement>(iconSelectors.join(','));
      existingIcons.forEach((el) => {
        console.log('  🗑️ 既存のfaviconを削除:', el.href);
        el.parentNode?.removeChild(el);
      });

      // 新しいfaviconを追加（複数の形式で確実に反映）
      const newIcon = document.createElement('link');
      newIcon.rel = 'icon';
      newIcon.type = 'image/png';
      newIcon.href = faviconUrl + '?v=' + Date.now(); // キャッシュバスター
      document.head.appendChild(newIcon);
      console.log('  ✅ favicon追加:', newIcon.href);

      const newShortcutIcon = document.createElement('link');
      newShortcutIcon.rel = 'shortcut icon';
      newShortcutIcon.type = 'image/png';
      newShortcutIcon.href = faviconUrl + '?v=' + Date.now();
      document.head.appendChild(newShortcutIcon);
      console.log('  ✅ shortcut icon追加:', newShortcutIcon.href);

      // iOSのホーム画面アイコンも設定
      const appleIcon = document.createElement('link');
      appleIcon.rel = 'apple-touch-icon';
      appleIcon.href = faviconUrl + '?v=' + Date.now();
      document.head.appendChild(appleIcon);
      console.log('  ✅ apple-touch-icon追加:', appleIcon.href);

      console.log('✅ PublicPageProvider: ファビコン設定完了');
    } else {
      console.warn('⚠️ PublicPageProvider: faviconが設定されていません');
    }

    // タブタイトルの設定（言語固有 > 共通設定の順で優先）
    const tabTitle = languageSpecificSettings?.tabTitle || data.siteSettings?.tabTitle;
    if (tabTitle) {
      console.log('🔄 PublicPageProvider: タブタイトルを設定中:', tabTitle);
      document.title = tabTitle;
      console.log('✅ PublicPageProvider: タブタイトル設定完了');
    }
  }, [data.siteSettings?.favicon, data.siteSettings?.tabTitle, languageSpecificSettings?.tabTitle]);

  // dataを更新（多言語関連の状態が変わったら）
  useEffect(() => {
    setData(prev => ({
      ...prev,
      selectedLanguage,
      availableLanguages,
      languageSpecificSettings,
      setSelectedLanguage
    }));
  }, [selectedLanguage, availableLanguages, languageSpecificSettings]);

  return (
    <PublicPageContext.Provider value={{ ...data, preferredLanguage: primaryLanguageCode(preferredLanguage) }}>
      {children}
    </PublicPageContext.Provider>
  );
};




